import type { JSX } from 'react'

import type { PriceExplanation } from '@lensmira/domain'

import { groupThousands } from '../money.js'
import type { Translate } from '../queue/status-line.js'

/**
 * Appendix V.1's structured breakdown, and the one formatter it is allowed to go through.
 * `PRC-011` `MUST`: "a visible price explanation on every quote".
 *
 * **It lives here because two screens render it and `PRC-011` names the second one.** `OrderDetail`
 * wrote it first and the quote screen needs the identical thing, which is the point at which a
 * second copy becomes the defect `status-labels.ts` exists to undo. The amounts are the reason it
 * matters more than wording would: `deltaMinor` and `runningMinor` are named "Minor" and are
 * **not** settled minor units. The engine's own `formatExact` rounds once, at its step 11 ("The
 * only rounding in the engine"), so every entry before that step is an exact fraction up to four
 * decimal places. Running one through `../money.ts`'s `parseMinorUnits` would both throw (it
 * accepts only `/^-?\d+$/`) and add a rounding step no screen has licence to take: `MR-04` spends
 * the whole "exactly three points" budget on the settled totals, none of them here. A duplicated
 * renderer is a second chance for somebody to reach for the wrong formatter.
 */

/**
 * A price-explanation entry's `deltaMinor` or `runningMinor`, thousands-grouped but otherwise
 * exactly as the pricing engine produced it.
 *
 * Falls back to the raw value, prefixed with the currency, on anything that does not match the
 * shape the engine's `formatExact` produces, rather than throwing: this is a supplementary
 * explanation of an already-settled total, not the total itself, so a shape it did not anticipate
 * is shown as best it can rather than taking down the line it explains.
 */
export function formatPriceExplanationAmount(raw: string, currency: string): string {
  const match = /^(-)?(\d+)(\.\d+)?$/.exec(raw)
  if (!match) return `${currency} ${raw}`
  const [, sign, whole, fraction] = match
  // Grouped by the shared helper, but deliberately not by `formatMoneyForDisplay`: these are the
  // engine's exact pre-rounding fractions, up to four decimal places, and `parseMinorUnits` would
  // refuse them.
  const grouped = groupThousands(whole ?? '0')
  return `${currency} ${sign ?? ''}${grouped}${fraction ?? ''}`
}

export interface PriceExplanationListProps {
  readonly t: Translate
  /** Only for keying the rows, so two lines' entries cannot collide. */
  readonly lineId: string
  readonly description: string
  readonly entries: readonly PriceExplanation[]
  readonly currency: string
}

/**
 * The breakdown for one line, rendered only when there are entries: what the counter shows when a
 * customer asks why this line costs what it does.
 */
export function PriceExplanationList({
  t,
  lineId,
  description,
  entries,
  currency,
}: PriceExplanationListProps): JSX.Element | null {
  if (entries.length === 0) return null
  return (
    <div className="lm-paste-report">
      <p className="lm-paste-report__line">{t('orderDetail.priceWhy', { description })}</p>
      <ul className="lm-price-explanation">
        {entries.map((entry, index) => (
          <li key={`${lineId}-${entry.step}-${index}`} className="lm-price-explanation__row">
            <span className="lm-price-explanation__label">{entry.label}</span>
            <span className="lm-price-explanation__delta" dir="ltr">
              <span className="lm-visually-hidden">{t('orderDetail.priceContributedSr')} </span>
              {formatPriceExplanationAmount(entry.deltaMinor, currency)}
            </span>
            <span className="lm-price-explanation__running" dir="ltr">
              <span className="lm-visually-hidden">{t('orderDetail.priceRunningSr')} </span>
              {formatPriceExplanationAmount(entry.runningMinor, currency)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
